<div class="matrix-shell p-2 p-lg-2">
  <section class="matrix-card">
    <div class="toolbar">
      <div class="toolbar-title">
        <strong>96-Block Fleet Operations Matrix</strong>
        <span *ngIf="selectedProduct === 'INTRADAY'">
          <lucide-icon [img]="BatteryChargingIcon" [size]="14"></lucide-icon>
          Current Block: <b>{{ currentWindowLabel() }}</b> (Block {{ currentBlock() }} of 96)
        </span>
      </div>
      <div class="toolbar-actions">
        <label class="control-field">
          <span>Product</span>
          <select class="form-select form-select-sm" [ngModel]="selectedProduct" (ngModelChange)="onProductChange($event)">
            <option value="INTRADAY">INTRADAY</option>
            <option value="DAY_AHEAD">DAY AHEAD</option>
          </select>
        </label>
        <label class="control-field threshold-field">
          <span>Threshold</span>
          <input class="form-control form-control-sm" [ngModel]="thresholdInput" (ngModelChange)="onThresholdChange($event)" placeholder="Enter." />
        </label>
        <label class="control-field date-field">
          <span>Date</span>
          <input class="form-control form-control-sm" type="date" [ngModel]="selectedDate" (ngModelChange)="onDateChange($event)" [disabled]="selectedProduct === 'INTRADAY'" />
        </label>
        <div class="forecast-sync">
          <button
            type="button"
            class="btn btn-sm btn-primary sync-button"
            (click)="refreshForecastPrices()"
            [disabled]="syncingForecastPrices()"
          >
            {{ syncingForecastPrices() ? 'Syncing Prices...' : 'Sync DAM / GDAM / RTM' }}
          </button>
          <span class="sync-caption" *ngIf="lastForecastPriceSyncLabel()">Last sync {{ lastForecastPriceSyncLabel() }}</span>
        </div>
        <div class="display-group">
          <span class="display-label">Display:</span>
          <label><input type="checkbox" [(ngModel)]="showSoc" /> SOC %</label>
          <label><input type="checkbox" [(ngModel)]="showPower" /> Power (MW)</label>
          <label><input type="checkbox" [(ngModel)]="showBidStatus" /> Bid Status</label>
          <span class="legend-chip"><i class="dot charge"></i> Charging</span>
          <span class="legend-chip"><i class="dot discharge"></i> Discharging</span>
          <span class="legend-chip"><i class="dot idle"></i> Idle</span>
          <span class="legend-chip"><i class="dot bid"></i> Bid Placed</span>
        </div>
      </div>
    </div>

    <div class="matrix-wrap" #matrixScroll>
      <table class="operations-table">
        <thead>
          <tr class="price-row" *ngFor="let priceBand of marketBands()">
            <th class="sticky-col sticky-sno sno-head sno-spacer" rowspan="3" *ngIf="priceBand.key === 'DAM'"></th>
            <th class="sticky-col sticky-plant left-head price-title-head" rowspan="3" *ngIf="priceBand.key === 'DAM'">
              Market Prices (₹/kWh)
            </th>
            <th class="sticky-col sticky-second sub-head">{{ priceBand.key }}</th>
            <th *ngFor="let block of blocks()" class="price-cell" [class.price-rtm]="priceBand.key === 'RTM'">
              {{ priceBand.values[block - 1] }}
            </th>
          </tr>
          <tr class="hour-row">
            <th class="sticky-col sticky-sno sno-head">S.No</th>
            <th class="sticky-col sticky-plant left-head hour-left-head">Plant / Time Block</th>
            <th class="sticky-col sticky-second sub-head hour-sub-head"></th>
            <th *ngFor="let hour of hourGroups()" class="block-head hour-head" [attr.colspan]="4">
              {{ hour }}
            </th>
          </tr>
          <tr class="minute-row">
            <th class="sticky-col sticky-sno sno-head minute-sno-head"> </th>
            <th class="sticky-col sticky-plant left-head minute-left-head"> </th>
            <th class="sticky-col sticky-second sub-head minute-sub-head"> </th>
            <th
              *ngFor="let minute of minuteLabels(); let index = index"
              #timeBlockHeader
              class="block-head minute-head"
              [class.current-block-head]="selectedProduct === 'INTRADAY' && index + 1 === currentBlock()"
            >
              {{ minute }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr *ngIf="loading()">
            <td class="empty-state" [attr.colspan]="99">Loading fleet operations matrix...</td>
          </tr>
          <tr *ngIf="!loading() && matrixRows().length === 0">
            <td class="empty-state" [attr.colspan]="99">No battery assets available. Add battery assets to populate this module.</td>
          </tr>
          <tr *ngFor="let row of matrixRows(); let rowIndex = index; trackBy: trackAssetRow">
            <td class="sticky-col sticky-sno sno-cell">{{ rowIndex + 1 }}</td>
            <td
              class="sticky-col sticky-plant asset-panel"
              [class.linked-asset-panel]="row.asset.id === linkedAssetId()"
              [class.asset-panel-collapsed]="!isAssetGraphVisible(row.asset.id)"
              (click)="toggleAssetGraph(row.asset.id)"
            >
              <div class="asset-head">
                <div class="asset-name">{{ row.vendorLabel }} ({{ row.asset.power_capacity_mw }} MW)</div>
                <div class="asset-current">SOC {{ row.currentBlockSoc | number:'1.0-0' }}%</div>
              </div>
              <!-- <div class="asset-meta">{{ row.asset.power_capacity_mw }} MW</div> -->
              <!-- <div class="asset-capacity">{{ row.vendorLabel }}</div> -->
              <div class="asset-expand-hint">{{ isAssetGraphVisible(row.asset.id) ? 'Hide graph' : '' }}</div>
              <div class="soc-graph-card" *ngIf="isAssetGraphVisible(row.asset.id)">
                <div class="soc-graph-head">
                  <span>Battery Charging Graph</span>
                  <strong>{{ row.currentBlockSoc | number:'1.0-0' }}%</strong>
                </div>
                <div class="battery-meter">
                  <div class="battery-meter-fill" [style.width.%]="row.currentBlockSoc"></div>
                  <span>{{ row.currentBlockSoc | number:'1.0-0' }}%</span>
                  <i class="battery-meter-cap"></i>
                </div>
                <svg class="soc-sparkline" viewBox="0 0 160 46" preserveAspectRatio="none" aria-hidden="true">
                  <path d="M0 45 L160 45" class="soc-grid-line"></path>
                  <path d="M0 4 L0 45" class="soc-guide-line"></path>
                  <path d="M40 4 L40 45" class="soc-guide-line"></path>
                  <path d="M80 4 L80 45" class="soc-guide-line"></path>
                  <path d="M120 4 L120 45" class="soc-guide-line"></path>
                  <path d="M160 4 L160 45" class="soc-guide-line"></path>
                  <path [attr.d]="row.socTrendAreaPath" class="soc-trend-area"></path>
                  <polyline [attr.points]="row.socTrendPoints" class="soc-trend-line"></polyline>
                </svg>
                <div class="soc-timeframe">
                  <span>00:00</span>
                  <span>06:00</span>
                  <span>12:00</span>
                  <span>18:00</span>
                  <span>24:00</span>
                </div>
                <div class="soc-scale">
                  <span>Start {{ row.socStart | number:'1.0-0' }}%</span>
                  <span>End {{ row.socEnd | number:'1.0-0' }}%</span>
                </div>
              </div>
              <div class="asset-kpis">
                <span><small>Charge</small><b class="charge-kpi-value">{{ row.totalChargeLakh | number:'1.1-1' }} L</b></span>
                <span><small>Revenue</small><b>{{ row.totalRevenueLakh | number:'1.1-1' }} L</b></span>
                <span><small>Profit</small><b>{{ row.totalProfitLakh | number:'1.1-1' }} L</b></span>
              </div>
            </td>
            <td class="sticky-col sticky-second asset-divider"></td>
            <td
              *ngFor="let cell of row.cells; trackBy: trackCell"
              class="matrix-cell"
              [class.charge-cell]="cell.action === 'charge'"
              [class.discharge-cell]="cell.action === 'discharge'"
              [class.idle-cell]="cell.action === 'idle'"
              [class.current-cell]="selectedProduct === 'INTRADAY' && cell.block === currentBlock()"
              [class.forecast-cell]="cell.source === 'forecast'"
              [class.bid-cell]="cell.bidPlaced"
              (mouseenter)="showCellTooltip($event, cell)"
              (mousemove)="moveCellTooltip($event, cell)"
              (mouseleave)="hideCellTooltip()"
            >
              <div class="matrix-cell__inner">
                <div class="cell-band">
                  <div class="cell-band__fill" [style.height.%]="batteryFillHeight(cell.soc)"></div>
                  <div class="cell-overlay">
                    <div class="cell-trend">
                      <svg *ngIf="cell.action !== 'idle'" class="cell-trend-icon" [class.cell-trend-icon--down]="cell.action === 'discharge'" [class.cell-trend-icon--up]="cell.action === 'charge'" viewBox="0 0 16 10" aria-hidden="true">
                        <polyline points="2,8 6,5 9,6 14,2"></polyline>
                      </svg>
                      <span *ngIf="cell.action === 'idle'" class="state-symbol idle-symbol">{{ statusGlyph(cell) }}</span>
                    </div>
                    <div class="cell-graph-value" *ngIf="showSoc">{{ formatSoc(cell.soc) }}</div>
                    <div class="cell-dot" *ngIf="showBidStatus" [class.bid-off]="!cell.bidPlaced"></div>
                    <div class="cell-sub" *ngIf="showPower">{{ formatPower(cell.power) }}</div>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      *ngIf="hoveredTooltip() as tooltip"
      class="matrix-tooltip"
      [style.left.px]="tooltip.left"
      [style.top.px]="tooltip.top"
    >
      <div class="matrix-tooltip__row">
        <span>Action:</span>
        <strong [class.is-charge]="tooltip.actionLabel === 'CHARGING'" [class.is-discharge]="tooltip.actionLabel === 'DISCHARGING'">
          {{ tooltip.actionLabel }}
        </strong>
      </div>
      <div class="matrix-tooltip__row"><span>Price:</span> <strong>{{ tooltip.priceLabel }}</strong></div>
      <div class="matrix-tooltip__row"><span>Quantity:</span> <strong>{{ tooltip.quantityLabel }}</strong></div>
      <div class="matrix-tooltip__row"><span>Predicted SOC:</span> <strong>{{ tooltip.socLabel }}</strong></div>
      <span class="matrix-tooltip__arrow"></span>
    </div>
  </section>
</div>
